import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { add } from 'date-fns/fp/add';
import { startOfToday } from 'date-fns/startOfToday';
import { DateValidationError } from '@mui/x-date-pickers';
import isEqual from 'lodash/fp/isEqual';
import isNull from 'lodash/fp/isNull';
import isInteger from 'lodash/fp/isInteger';
import writtenNumber from 'written-number';

import { ModalV2, ButtonV2, Switch, DatePicker, dsV2 } from '@bloomays-lib/ui.shared';
import type {
  CreateMissionAmendmentGQLInput,
  IContact,
  IMissionCardDetails,
  IMissionCardPricing,
} from '@bloomays-lib/types.shared';
import {
  CREATE_MISSION_AMENDMENT,
  GET_EXTENSION_ASSESSMENT,
  GET_LEGAL_CONTRACTS_BY_MISSION,
} from '@bloomays-lib/adapter.api-bloomer';
import { convertDateToStringFormat, getFirstValidPrice, greaterDate } from '@bloomays-lib/utils.shared';
import { monitorException } from '@bloomays-lib/common.monitoring/browser';
import { GraphQLError } from 'graphql';

import { cancel, notify } from '../../../../helpers/toastify';
import { extractApolloErrorCode } from '@bloomays-lib/utils.shared';
import { Logger } from '../../../../services/serviceLogger';
import {
  shouldDisableEndDate,
  shouldDisableConfirmDate,
  getFirstDayOfPickedMonth,
  getFirstAllowedPricingMonth,
} from '../../../molecules/CreateAmendment';
import { FieldV2, InputV2, TextareaV2, Row2, InfoV2, DividerV2, ToggleSection, SectionHeading } from '../formFields';

const logger = Logger('AmendmentModal');

export type AmendmentModalProps = {
  open: boolean;
  onClose: () => void;
  mission: IMissionCardDetails;
  legalClientContact?: IContact;
  legalClientSourceLabel?: string;
  legalBloomerContact?: IContact;
  legalBloomerSourceLabel?: string;
  legalContactUnavailableText?: string;
};

const formatDisplayDate = (date: Date | string) => convertDateToStringFormat(date, 'dd-MM-yyyy').replaceAll('-', '/');

const SignerCard = ({
  title,
  contact,
  sourceLabel,
  unavailableText,
}: {
  title: string;
  contact?: IContact;
  sourceLabel?: string;
  unavailableText?: string;
}) => {
  const { t } = useTranslation(['missionDashboard']);
  return (
    <Box
      sx={{
        border: `1px solid ${dsV2.color.border}`,
        borderRadius: dsV2.radius.sm,
        padding: '14px 16px',
        background: dsV2.color.surface,
      }}
    >
      <Box sx={{ fontFamily: dsV2.font.serif, fontSize: '16.5px', color: dsV2.color.noir, marginBottom: '11px' }}>
        {title}
      </Box>
      {sourceLabel ? (
        <Box
          sx={{
            fontSize: '11.5px',
            color: dsV2.color.t3,
            marginBottom: '11px',
            paddingBottom: '10px',
            borderBottom: `1px solid ${dsV2.color.borderLight}`,
          }}
        >
          {t('actionModals.amendment.sourcePrefix', { ns: 'missionDashboard' })}
          {sourceLabel}
        </Box>
      ) : null}
      {contact ? (
        <Box sx={{ fontSize: 12, color: dsV2.color.t2, lineHeight: 1.6 }}>
          <Box sx={{ fontWeight: 600, color: dsV2.color.noir }}>
            {contact.fullname || `${contact.firstname ?? ''} ${contact.lastname ?? ''}`.trim()}
          </Box>
          {contact.nameJob ? <Box sx={{ color: dsV2.color.t4 }}>{contact.nameJob}</Box> : null}
          {contact.email ? <Box sx={{ color: dsV2.color.bleu, wordBreak: 'break-all' }}>{contact.email}</Box> : null}
        </Box>
      ) : (
        <Box sx={{ fontSize: 12, color: dsV2.color.t4 }}>
          {unavailableText ?? t('actionModals.amendment.noContactFallback', { ns: 'missionDashboard' })}
        </Box>
      )}
    </Box>
  );
};

/**
 * Avenant de mission (ModalV2) — refonte V2 de `CreateAmendment`. Réutilise intégralement la
 * mutation `CREATE_MISSION_AMENDMENT`, le calcul des clauses, et la validation d'origine
 * (prolongation et/ou modification de TJM, signataires SST/PDS, garde-fous de dates et prix).
 */
export const AmendmentModal = ({
  open,
  onClose,
  mission,
  legalClientContact,
  legalClientSourceLabel,
  legalBloomerContact,
  legalBloomerSourceLabel,
  legalContactUnavailableText,
}: AmendmentModalProps) => {
  const { t } = useTranslation(['extendShortenMission', 'notify', 'missionDashboard']);
  const endDate = useMemo(() => new Date(mission?.endMission as string), [mission]);

  const [extendState, setExtendState] = useState(true);
  const [pricingState, setPricingState] = useState(false);

  const [newEndMission, setNewEndMission] = useState<Date | null>(null);
  const [confirmNewEndMission, setConfirmNewEndMission] = useState<Date | null>(null);
  const [firstClause, setFirstClause] = useState<string>(t('defaultFirstClause'));

  const [newPurchasePrice, setNewPurchasePrice] = useState<number | null>(null);
  const [newSalePrice, setNewSalePrice] = useState<number | null>(null);
  const [newPricingDate, setNewPricingDate] = useState<Date | null>(null);
  const [secondClauseForSST, setSecondClauseForSST] = useState<string>(t('defaultSecondClause'));
  const [secondClauseForPDS, setSecondClauseForPDS] = useState<string>(t('defaultSecondClause'));

  const [endDateError, setEndDateError] = useState<DateValidationError | null>(null);
  const [confirmDateError, setConfirmDateError] = useState<DateValidationError | null>(null);
  const [invalidPrice, setInvalidPrice] = useState<{ purchasePrice: boolean; salePrice: boolean }>({
    purchasePrice: false,
    salePrice: false,
  });
  const [useAmendmentTemplate, setUseAmendmentTemplate] = useState<{
    useTemplateForSST: boolean;
    useTemplateForPDS: boolean;
  }>({ useTemplateForSST: true, useTemplateForPDS: mission.contractType === 'bloomaysContract' });

  const [createMissionAmendment, { loading }] = useMutation(CREATE_MISSION_AMENDMENT, {
    refetchQueries: [
      { query: GET_EXTENSION_ASSESSMENT, variables: { missionId: mission.recordId } },
      { query: GET_LEGAL_CONTRACTS_BY_MISSION, variables: { missionId: mission.recordId } },
    ],
  });

  const activePricing = useMemo(
    () =>
      getFirstValidPrice(mission.prices || [], greaterDate(new Date(mission?.beginMission as string), new Date())) as
        | IMissionCardPricing
        | undefined,
    [mission],
  );
  const minPricingDate = useMemo(() => getFirstAllowedPricingMonth(), []);

  useEffect(() => {
    if (!newEndMission || !confirmNewEndMission) return;
    const dayAfterEndMission = add({ days: 1 }, new Date(mission.endMission));
    if (newEndMission.getTime() === confirmNewEndMission.getTime()) {
      setFirstClause(
        t('firstClause', {
          dayAfterCurrentEndMission: formatDisplayDate(dayAfterEndMission),
          newEndMission: formatDisplayDate(newEndMission),
          missionLabel: mission.label,
          bloomerFullName: `${mission.bloomer.firstname} ${mission.bloomer.lastname}`,
        }),
      );
    }
  }, [newEndMission, confirmNewEndMission, t, mission]);

  useEffect(() => {
    if (!newPricingDate || !newSalePrice || !newPurchasePrice) return;
    const pricingText = (price: number) =>
      t('secondClause', {
        price,
        priceInLetters: writtenNumber(price, { lang: 'fr' }),
        pricingDate: formatDisplayDate(newPricingDate),
      });
    setSecondClauseForSST(
      Number(newPurchasePrice) === Number(activePricing?.purchasePricePerDayWithoutTaxes)
        ? t('defaultSecondClause')
        : pricingText(newPurchasePrice),
    );
    setSecondClauseForPDS(
      Number(newSalePrice) === Number(activePricing?.salePricePerDayWithoutTaxes)
        ? t('defaultSecondClause')
        : pricingText(newSalePrice),
    );
  }, [newPricingDate, newPurchasePrice, newSalePrice, t, activePricing]);

  const invalidPricing = invalidPrice.purchasePrice || invalidPrice.salePrice;
  const uncompletePricing =
    (pricingState && (isNull(newPurchasePrice) || isNull(newSalePrice) || isNull(newPricingDate))) || invalidPricing;
  const uncompleteExtend = extendState && (isNull(newEndMission) || isNull(confirmNewEndMission));
  const purchasePriceNoChange = newPurchasePrice === activePricing?.purchasePricePerDayWithoutTaxes;
  const salePriceNoChange = newSalePrice === activePricing?.salePricePerDayWithoutTaxes;
  const pricingNoChange = purchasePriceNoChange && salePriceNoChange;
  const disableSSTSwitch = !extendState && purchasePriceNoChange;
  const disablePDSSwitch = !extendState && salePriceNoChange;
  const isBloomaysContract = mission.contractType === 'bloomaysContract';

  // Libellé lisible du type de contrat ; repli sur la variante générique si le type n'a pas de
  // libellé i18n (ex. nouveau type non encore mappé) pour ne jamais afficher la clé brute.
  const contractTypeLabel = mission.contractType
    ? t(`missionContractTypeLabel.${mission.contractType}`, { defaultValue: '' })
    : '';
  const pdsNotAutomaticDescriptionHtml = contractTypeLabel
    ? t('pdsNotAutomaticDescription', { contractTypeLabel })
    : t('pdsNotAutomaticDescriptionUnknownType');

  const differentExtendDates =
    extendState &&
    !isNull(newEndMission) &&
    !isNull(confirmNewEndMission) &&
    !isEqual(newEndMission, confirmNewEndMission);
  const hasDateError = extendState && (Boolean(endDateError) || Boolean(confirmDateError));
  const missingSstContact = !legalBloomerContact;
  const missingPdsContact = isBloomaysContract && !legalClientContact;

  const endDateErrorLabel = useMemo(() => {
    if (endDateError === 'minDate' || endDateError === 'shouldDisableDate')
      return t('dateErrorMinEndDate', { minDate: formatDisplayDate(endDate) });
    if (endDateError === 'invalidDate') return t('dateErrorInvalidDate');
    return '';
  }, [endDateError, endDate, t]);

  const confirmDateErrorLabel = useMemo(() => {
    if (confirmDateError === 'minDate') return t('dateErrorMinConfirmDate');
    if (confirmDateError === 'maxDate' || confirmDateError === 'shouldDisableDate') return t('dateErrorMaxConfirmDate');
    if (confirmDateError === 'invalidDate') return t('dateErrorInvalidDate');
    return '';
  }, [confirmDateError, t]);

  const contractSSTAmendmentLabel =
    mission.contractType === 'bloomaysContract'
      ? 'generateAutomaticSSTContractAmendmentBloomays'
      : 'generateAutomaticSSTContractAmendmentNotBloomays';

  const disabledButton =
    (!pricingState && !extendState) ||
    loading ||
    uncompletePricing ||
    uncompleteExtend ||
    pricingNoChange ||
    differentExtendDates ||
    hasDateError ||
    missingSstContact ||
    missingPdsContact;

  const handleError = (graphqlErrors: [GraphQLError], context?: object) => {
    const graphqlError = graphqlErrors?.[0];
    const errorCode = extractApolloErrorCode(graphqlError);
    if (errorCode === 'BAD_USER_INPUT') {
      const invalidArgs = graphqlError.extensions?.invalidArgs as Array<{ missing: string }>;
      if (graphqlError?.message) {
        notify('error', t('missingFieldError', { ns: 'notify', missingField: graphqlError.message }));
      } else {
        notify('error', t('missingFieldError', { ns: 'notify', missingField: invalidArgs[0].missing as string }));
      }
    } else if (errorCode === 'CREATE_MISSION_AMENDMENT_ERROR') {
      const errorMessage = graphqlError?.message;
      const meta = graphqlError.extensions?.meta as { referentialContractSignerError?: boolean } | undefined;
      logger.error(errorMessage, { context }, graphqlError);
      monitorException(graphqlError, { context });
      notify(
        'error',
        meta?.referentialContractSignerError
          ? errorMessage
          : `${errorMessage}${t('actionModals.amendment.contactTechSuffix', { ns: 'missionDashboard' })}`,
      );
    } else if (errorCode === 'REFERENTIAL_INCONSISTENCY' || errorCode === 'MISSING_REFERENTIAL_DATA') {
      notify('error', graphqlError?.message || t('randomUpdateError', { ns: 'notify' }));
    } else {
      notify('error', t('randomUpdateError', { ns: 'notify' }));
    }
  };

  const createAmendment = async () => {
    let input: CreateMissionAmendmentGQLInput | undefined;
    try {
      notify('info', t('updateInProgress', { ns: 'notify' }));
      input = {
        recordId: mission.recordId,
        newEndMission: extendState ? convertDateToStringFormat(newEndMission) : null,
        useTemplateForSST: disableSSTSwitch ? false : useAmendmentTemplate.useTemplateForSST,
        useTemplateForPDS: disablePDSSwitch ? false : useAmendmentTemplate.useTemplateForPDS,
        newPurchasePrice: pricingState ? newPurchasePrice : null,
        newSalePrice: pricingState ? newSalePrice : null,
        newPricingDate: pricingState ? convertDateToStringFormat(newPricingDate) : null,
        firstClause: extendState ? firstClause : t('defaultFirstClause'),
        secondClauseForSST: pricingState ? secondClauseForSST : t('defaultSecondClause'),
        secondClauseForPDS: pricingState ? secondClauseForPDS : t('defaultSecondClause'),
      };
      const response = await createMissionAmendment({ variables: { input } });
      if (response?.data.createMissionAmendment) {
        cancel();
        notify('success', t('updateSuccess', { ns: 'notify' }));
        onClose();
      } else {
        cancel();
        handleError(response.errors as [GraphQLError], { input, response });
      }
    } catch (e: any) {
      cancel();
      handleError(e.graphQLErrors as [GraphQLError], { input, error: e });
    }
  };

  return (
    <ModalV2
      open={open}
      onClose={onClose}
      icon={<AutoAwesomeRoundedIcon sx={{ fontSize: 18 }} />}
      iconTone="violet"
      title={t('actionModals.amendment.title', { ns: 'missionDashboard' })}
      width={780}
      footer={
        <>
          <ButtonV2 variant="secondary" onClick={onClose}>
            {t('cancel', { ns: 'random' })}
          </ButtonV2>
          <ButtonV2
            onClick={createAmendment}
            disabled={disabledButton}
            loading={loading}
            startIcon={<SendRoundedIcon sx={{ fontSize: 15 }} />}
          >
            {t('sendAmendment')}
          </ButtonV2>
        </>
      }
    >
      <ToggleSection
        title={t('extendTitle')}
        description={t('newDateText')}
        checked={extendState}
        onChange={setExtendState}
      >
        <Row2>
          <FieldV2 label={t('pickDate')} error={endDateErrorLabel || undefined}>
            <DatePicker
              shouldDisableDate={shouldDisableEndDate(endDate)}
              minDate={endDate}
              label={t('pickDate')}
              value={newEndMission}
              handleChange={setNewEndMission}
              onError={(error) => setEndDateError(error)}
            />
          </FieldV2>
          <FieldV2 label={t('pickConfirmDate')} error={confirmDateErrorLabel || undefined}>
            <DatePicker
              shouldDisableDate={shouldDisableConfirmDate(newEndMission)}
              minDate={startOfToday()}
              maxDate={newEndMission ?? undefined}
              label={t('pickConfirmDate')}
              value={confirmNewEndMission}
              handleChange={setConfirmNewEndMission}
              onError={(error) => setConfirmDateError(error)}
            />
          </FieldV2>
        </Row2>
        {differentExtendDates ? <InfoV2 tone="amber">{t('errorExtendDescription')}</InfoV2> : null}
        <FieldV2
          label={t('actionModals.amendment.article1Label', { ns: 'missionDashboard' })}
          hint={t('durationHelperText')}
        >
          <TextareaV2 value={firstClause} maxLength={290} onChange={setFirstClause} />
        </FieldV2>
      </ToggleSection>

      <DividerV2 />

      <ToggleSection
        title={t('modifyPricing')}
        description={t('newPricingText')}
        checked={pricingState}
        onChange={setPricingState}
      >
        <Row2>
          <FieldV2 label={t('actionModals.amendment.currentPurchaseTjmLabel', { ns: 'missionDashboard' })}>
            <InputV2 type="number" value={activePricing?.purchasePricePerDayWithoutTaxes ?? ''} disabled />
          </FieldV2>
          <FieldV2
            label={t('actionModals.amendment.newPurchaseTjmLabel', { ns: 'missionDashboard' })}
            error={invalidPrice.purchasePrice ? t('invalidPricingTitle') : undefined}
          >
            <InputV2
              type="number"
              value={newPurchasePrice ?? ''}
              onChange={(raw) => {
                if (isInteger(Number(raw))) {
                  setInvalidPrice((p) => ({ ...p, purchasePrice: false }));
                  setNewPurchasePrice(Number(raw));
                } else {
                  setInvalidPrice((p) => ({ ...p, purchasePrice: true }));
                }
              }}
            />
          </FieldV2>
        </Row2>
        <Row2>
          <FieldV2 label={t('actionModals.amendment.currentSaleTjmLabel', { ns: 'missionDashboard' })}>
            <InputV2 type="number" value={activePricing?.salePricePerDayWithoutTaxes ?? ''} disabled />
          </FieldV2>
          <FieldV2
            label={t('actionModals.amendment.newSaleTjmLabel', { ns: 'missionDashboard' })}
            error={invalidPrice.salePrice ? t('invalidPricingTitle') : undefined}
          >
            <InputV2
              type="number"
              value={newSalePrice ?? ''}
              onChange={(raw) => {
                if (isInteger(Number(raw))) {
                  setInvalidPrice((p) => ({ ...p, salePrice: false }));
                  setNewSalePrice(Number(raw));
                } else {
                  setInvalidPrice((p) => ({ ...p, salePrice: true }));
                }
              }}
            />
          </FieldV2>
        </Row2>
        <FieldV2 label={t('pickPricingDate')}>
          <DatePicker
            label={t('pickPricingDate')}
            value={newPricingDate}
            handleChange={(e) => setNewPricingDate(getFirstDayOfPickedMonth(e))}
            minDate={minPricingDate}
            views={['year', 'month']}
            openTo="month"
          />
        </FieldV2>
        {pricingNoChange ? <InfoV2 tone="amber">{t('errorPricingDescription')}</InfoV2> : null}
        <FieldV2 label={t('actionModals.amendment.bloomerArticle2Label', { ns: 'missionDashboard' })}>
          <TextareaV2
            value={secondClauseForSST}
            maxLength={290}
            disabled={disableSSTSwitch}
            onChange={setSecondClauseForSST}
          />
        </FieldV2>
        <FieldV2
          label={t('actionModals.amendment.clientArticle2Label', { ns: 'missionDashboard' })}
          hint={t('pricingHelperText')}
        >
          <TextareaV2
            value={secondClauseForPDS}
            maxLength={290}
            disabled={disablePDSSwitch}
            onChange={setSecondClauseForPDS}
          />
        </FieldV2>
      </ToggleSection>

      <DividerV2 />

      {!pricingState && !extendState ? (
        <InfoV2 tone="blue">{t('missingChoiceDescription')}</InfoV2>
      ) : (
        <>
          <SectionHeading>{t('automaticAmendment')}</SectionHeading>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
            <Switch
              id="useTemplateForSST"
              disabled={disableSSTSwitch}
              checked={disableSSTSwitch ? false : useAmendmentTemplate.useTemplateForSST}
              onSwitch={(_e, checked) => setUseAmendmentTemplate((p) => ({ ...p, useTemplateForSST: checked }))}
              label={t(contractSSTAmendmentLabel)}
            />
            {isBloomaysContract ? (
              <Switch
                id="useTemplateForPDS"
                disabled={disablePDSSwitch}
                checked={disablePDSSwitch ? false : useAmendmentTemplate.useTemplateForPDS}
                onSwitch={(_e, checked) => setUseAmendmentTemplate((p) => ({ ...p, useTemplateForPDS: checked }))}
                label={t('generateAutomaticPDSContractAmendmentBloomays')}
              />
            ) : null}
          </Box>
          {!isBloomaysContract ? (
            <InfoV2 tone="blue">
              <Box sx={{ fontWeight: 600, marginBottom: '2px' }}>{t('pdsNotAutomaticTitle')}</Box>
              <span
                dangerouslySetInnerHTML={{
                  __html: pdsNotAutomaticDescriptionHtml,
                }}
              />
            </InfoV2>
          ) : null}
          <SectionHeading>{t('defaultSignersTitle')}</SectionHeading>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '12px' }}>
            <SignerCard
              title={t('defaultSstSigner')}
              contact={legalBloomerContact}
              sourceLabel={legalBloomerSourceLabel}
              unavailableText={legalContactUnavailableText}
            />
            {isBloomaysContract ? (
              <SignerCard
                title={t('defaultPdsSigner')}
                contact={legalClientContact}
                sourceLabel={legalClientSourceLabel}
                unavailableText={legalContactUnavailableText}
              />
            ) : null}
          </Box>
          {missingSstContact ? <InfoV2 tone="amber">{t('missingSstContactDescription')}</InfoV2> : null}
          {missingPdsContact ? <InfoV2 tone="amber">{t('missingPdsContactDescription')}</InfoV2> : null}
        </>
      )}
    </ModalV2>
  );
};

export default AmendmentModal;
